import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ApiStatus} from '../../../models';
import {IState} from '../../../store/reducers';
import {CONTEXT_TYPE} from '../constants';
import {loadPreview, unsetPreview} from '../../../store/actions/blockActions';
import {useSourceId, useSourceType} from '../SourceContext';

interface PreviewObject {
  isPreview: boolean | undefined; /* if undefined, it means it was never set */
  data: any | null;
  executePreview: (value?: any) => void;
  loading: boolean;
}

export function usePreviewBlock(
  uid: string | undefined,
): PreviewObject {
  /*
   * if block has JINJA variables, parse them and preview block
   */
  const [isPreview, setIsPreview] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const previewStatus = useSelector((state: IState) => state.block.previewStatus);
  const previews = useSelector((state: IState) => state.block.previews);

  /*
   * If we have preview data, it is in preview mode
   */
  const data = useMemo(() => uid ? (previews?.[uid] || null) : null, [previews]);
  const sourceId = useSourceId();
  const sourceType = useSourceType();
  const dispatch = useDispatch();

  useEffect(() => {
    /*
     * If execution is finished, reset executing flag.
     */
    if (loading && previewStatus !== ApiStatus.LOADING) {
      if (
        previewStatus === ApiStatus.LOADED ||
        previewStatus === ApiStatus.FAILED
      ) {
        setLoading(false);
      }
      if (previewStatus === ApiStatus.LOADED) {
        setIsPreview(true);
      }
    }
  }, [uid, loading, previewStatus]);

  const executePreview = (block?: any) => {
    /*
     * Toggle preview:
     * - send request to preview block
     * - if `preview` is `true`, unset preview
     */

    if (loading || !uid) {
      return;
    }

    if (isPreview) {
      setIsPreview(false);
      dispatch(unsetPreview(uid));
      return;
    }

    setLoading(true);

    const reportUid = sourceType === CONTEXT_TYPE.REPORT ? sourceId as string : '';

    dispatch(loadPreview(uid, reportUid, {}));
  };

  return {
    isPreview,
    data,
    loading,
    executePreview,
  }
}
